import { History, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Confirm } from '@/components/ui/Confirm';
import { Pill } from '@/components/ui/Pill';
import { useSession } from '@/lib/auth/useSession';
import { formatDateTime } from '@/lib/format';
import { DEFAULT_MAX_SEARCHES, MAX_SEARCHES_RANGE, type PromptVersion } from './api';
import { useActivateVersion, useSaveVersion, useVersions } from './queries';

/**
 * Instructions editor with revision history (P1-D9).
 *
 * Edits Simon's GUIDANCE only — the part SREG owns (how to read SREG's data,
 * preferences). Simon's system instructions (identity, citation and tool
 * rules) are fixed in code and always come first. Saving creates a new
 * version and makes it active; activating an older version rolls back.
 */
export function InstructionsScreen() {
  const versions = useVersions();
  const active = useMemo(() => versions.data?.find((v) => v.active), [versions.data]);
  const [viewing, setViewing] = useState<PromptVersion | null>(null);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 py-8 lg:grid-cols-[1fr_320px]">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Instructions</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-soft">
            Guidance Simon follows when answering — how to read SREG's data, what to prefer, what to flag. It's added
            after Simon's built-in rules (citing sources, never inventing terms), which aren't editable here. Changes
            apply to new answers within about a minute.
          </p>
          {versions.isLoading ? (
            <p className="mt-6 text-sm text-ink-faint">Loading…</p>
          ) : (
            <Editor
              key={viewing?.id ?? active?.id ?? 'new'}
              base={viewing ?? active}
              isViewingOld={!!viewing && !viewing.active}
              onDone={() => setViewing(null)}
            />
          )}
        </div>
        <VersionHistory versions={versions.data ?? []} viewingId={viewing?.id} onView={setViewing} />
      </div>
    </div>
  );
}

function Editor({ base, isViewingOld, onDone }: { base?: PromptVersion; isViewingOld: boolean; onDone: () => void }) {
  const { locked } = useSession();
  const lock = locked('instructions.write');
  const save = useSaveVersion();
  const [content, setContent] = useState(base?.content ?? '');
  const [versionName, setVersionName] = useState('');
  const [note, setNote] = useState('');
  const baseMax = base?.maxSearches ?? DEFAULT_MAX_SEARCHES;
  const [maxSearches, setMaxSearches] = useState(String(baseMax));
  useEffect(() => setContent(base?.content ?? ''), [base?.content]);
  useEffect(() => setMaxSearches(String(baseMax)), [baseMax]);

  const maxValue = Number(maxSearches);
  const maxValid = Number.isInteger(maxValue) && maxValue >= MAX_SEARCHES_RANGE.min && maxValue <= MAX_SEARCHES_RANGE.max;
  const dirty = content.trim() !== (base?.content ?? '').trim() || (maxValid && maxValue !== baseMax);

  return (
    <div className="mt-6 rounded-xl border border-line bg-surface p-4">
      <div className="mb-2 flex items-center gap-2 text-xs text-ink-faint">
        {base ? (
          <>
            {isViewingOld ? 'Editing from' : 'Active:'}{' '}
            <Pill tone={base.active ? 'sage' : 'neutral'}>v{base.version}</Pill>
            {base.versionName && <span className="font-semibold text-ink-soft">{base.versionName}</span>}
            {isViewingOld && (
              <button type="button" onClick={onDone} className="ml-auto font-semibold text-sage hover:underline">
                Back to active
              </button>
            )}
          </>
        ) : (
          <span>No saved guidance yet — Simon is using its built-in default. Saving creates version 1.</span>
        )}
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        readOnly={!!lock}
        rows={22}
        spellCheck
        aria-label="Guidance"
        className="w-full resize-y rounded-lg border border-line bg-surface-alt p-3 font-mono text-[13px] leading-relaxed text-ink outline-none focus:border-sage"
      />
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          value={versionName}
          onChange={(e) => setVersionName(e.target.value)}
          disabled={!!lock}
          placeholder="Version name (optional), e.g. “Sept close rules”"
          aria-label="Version name"
          className="rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-sage disabled:bg-surface-alt"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={!!lock}
          placeholder="What changed and why (optional)"
          aria-label="Change note"
          className="rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-sage disabled:bg-surface-alt"
        />
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
        Document searches per answer
        <input
          type="number"
          min={MAX_SEARCHES_RANGE.min}
          max={MAX_SEARCHES_RANGE.max}
          step={1}
          value={maxSearches}
          onChange={(e) => setMaxSearches(e.target.value)}
          disabled={!!lock}
          aria-label="Document searches per answer"
          aria-invalid={!maxValid}
          className="w-16 rounded-lg border border-line px-2 py-1 text-sm text-ink outline-none focus:border-sage disabled:bg-surface-alt aria-[invalid=true]:border-bronze"
        />
        <span className="text-xs text-ink-faint">
          {MAX_SEARCHES_RANGE.min}–{MAX_SEARCHES_RANGE.max}. More can find more, but answers take longer. Saved with this version.
        </span>
      </label>
      <div className="mt-3 flex items-center justify-end gap-3">
        {save.isSuccess && !dirty && (
          <span className="text-xs text-sage-dark">Saved as v{save.data.version} — now active.</span>
        )}
        <Button
          variant="secondary"
          disabled={!dirty || save.isPending}
          lockedReason={lock}
          onClick={() => {
            setContent(base?.content ?? '');
            setMaxSearches(String(baseMax));
          }}
        >
          Discard changes
        </Button>
        <Button
          lockedReason={lock}
          disabled={!dirty || !content.trim() || !maxValid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                content,
                versionName: versionName.trim() || undefined,
                note: note.trim() || undefined,
                maxSearches: maxValue,
              },
              {
                onSuccess: () => {
                  setVersionName('');
                  setNote('');
                  onDone();
                },
              },
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save as new version'}
        </Button>
      </div>
    </div>
  );
}

function VersionHistory({
  versions,
  viewingId,
  onView,
}: {
  versions: PromptVersion[];
  viewingId?: string;
  onView: (v: PromptVersion) => void;
}) {
  return (
    <aside className="lg:pt-16">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
        <History size={15} /> History
      </h2>
      {versions.length === 0 && <p className="mt-3 text-sm text-ink-faint">No versions yet.</p>}
      <ol className="mt-3 space-y-2">
        {versions.map((v) => (
          <VersionItem key={v.id} v={v} selected={v.id === viewingId} onView={() => onView(v)} />
        ))}
      </ol>
    </aside>
  );
}

function VersionItem({ v, selected, onView }: { v: PromptVersion; selected: boolean; onView: () => void }) {
  const { locked } = useSession();
  const activate = useActivateVersion();
  const [confirming, setConfirming] = useState(false);
  return (
    <li className={`relative rounded-lg border bg-surface p-3 ${selected ? 'border-sage' : 'border-line'}`}>
      <div className="flex items-center gap-2">
        <Pill tone={v.active ? 'sage' : 'neutral'}>v{v.version}</Pill>
        {v.active && <span className="text-xs font-semibold text-sage-dark">Active</span>}
        <span className="ml-auto text-xs text-ink-faint">{formatDateTime(v.createdAt)}</span>
      </div>
      {v.versionName && <p className="mt-1 text-sm font-semibold text-ink">{v.versionName}</p>}
      {v.note && <p className="mt-0.5 text-xs text-ink-soft">{v.note}</p>}
      <p className="mt-0.5 text-xs text-ink-faint">{v.maxSearches ?? DEFAULT_MAX_SEARCHES} searches per answer</p>
      <div className="mt-2 flex gap-3 text-xs font-semibold">
        <button type="button" onClick={onView} className="text-sage hover:underline">
          View
        </button>
        {!v.active && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={!!locked('instructions.write')}
            title={locked('instructions.write')}
            className="flex items-center gap-1 text-ink-soft hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RotateCcw size={12} /> Make active
          </button>
        )}
      </div>
      {confirming && (
        <Confirm
          title={`Make v${v.version} active?`}
          body="Simon uses this version for new answers. The current version stays in history."
          confirmLabel="Make active"
          busy={activate.isPending}
          className="right-0 top-full mt-1"
          onCancel={() => setConfirming(false)}
          onConfirm={() => activate.mutate(v.id, { onSettled: () => setConfirming(false) })}
        />
      )}
    </li>
  );
}
